import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { api, type Bench, type ProductDto, type PublicSnapshot } from "../api";
import { useCart } from "../state/cart";
import { money, Photo, Spinner } from "../ui";
import { ProductSheet } from "./ProductSheet";
import { CartSheet } from "./CartSheet";

type Banner = PublicSnapshot["banners"][number];

/**
 * Цвет подписи поверх заливки: белый на тёмной, угольный на светлой.
 *
 * Порог взят по воспринимаемой яркости, а не по среднему трёх каналов: глаз
 * считает зелёный вчетверо ярче синего, и по среднему насыщенно-зелёная кнопка
 * выходит «тёмной», получает белую подпись и становится нечитаемой.
 */
function readableOn(hex: string): string {
  const value = hex.replace("#", "");
  if (value.length !== 6) return "#ffffff";

  const channel = (at: number) => parseInt(value.slice(at, at + 2), 16) / 255;
  const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);

  return luminance > 0.6 ? "#1b1e20" : "#ffffff";
}

export function MenuScreen({ benchRef }: { benchRef: string }) {
  const [state, setState] = useState<PublicSnapshot | null>(null);
  const [bench, setBench] = useState<Bench | null>(null);
  const [benchChecked, setBenchChecked] = useState(false);
  const [activeCategory, setActiveCategory] = useState("");
  const [chosen, setChosen] = useState<ProductDto | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");

  const cart = useCart(promoCode);

  const sections = useRef(new Map<string, HTMLElement>());
  const railRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const load = () => api.get<PublicSnapshot>("/api/public/state").then(setState).catch(() => {});
    void load();

    // Стоп-лист и режим работы меняются на ходу — меню обязано это показывать (п. 29 ТЗ).
    const stream = new EventSource("/api/public/events");
    stream.addEventListener("state", event => {
      const parsed = JSON.parse((event as MessageEvent<string>).data) as { payload: PublicSnapshot | null };
      if (parsed.payload) setState(parsed.payload);
    });

    return () => stream.close();
  }, []);

  const colors = state?.storefront.colors;

  useEffect(() => {
    if (!colors) return;

    /*
     * Переменные ставятся на корень документа, а не на саму страницу: фон
     * красит `body`, и переопределение ниже по дереву до него не доходит.
     * Вёрстка при этом продолжает читать те же токены и о подмене не знает.
     */
    const root = document.documentElement;
    const applied: [string, string][] = [];

    if (colors.bg) applied.push(["--paper", colors.bg]);
    if (colors.ink) applied.push(["--ink", colors.ink]);

    if (colors.accent) {
      applied.push(["--accent", colors.accent]);
      // Подпись поверх заливки выбирается по яркости самой заливки, а не
      // назначается заранее: владелец волен взять и тёмно-вишнёвый, и жёлтый,
      // и белые буквы на втором пропадают вместе с ценой.
      applied.push(["--accent-ink", readableOn(colors.accent)]);
    }

    for (const [name, value] of applied) root.style.setProperty(name, value);

    return () => {
      for (const [name] of applied) root.style.removeProperty(name);
    };
  }, [colors]);

  useEffect(() => {
    if (!benchRef) {
      setBenchChecked(true);
      return;
    }

    api
      .get<Bench>(`/api/public/benches/${encodeURIComponent(benchRef)}`)
      .then(setBench)
      .catch(() => setBench(null))
      .finally(() => setBenchChecked(true));
  }, [benchRef]);

  const categories = state?.categories ?? [];
  const products = state?.products ?? [];
  const banners = state?.banners ?? [];

  /*
   * Меню показывается целиком, а полоса категорий его листает.
   *
   * Раньше нажатие на категорию прятало остальные разделы. На телефоне это
   * выглядело как исчезновение половины меню, и вернуть его догадывались не
   * все. Прокрутка оставляет меню на месте — и заодно даёт баннеру цель,
   * к которой можно вести.
   */
  const grouped = useMemo(
    () =>
      categories
        .map(category => ({
          category,
          items: products.filter(product => product.categoryId === category.id),
          before: banners.filter(banner => banner.placement === "section" && banner.anchorSlug === category.id),
          inside: banners.filter(banner => banner.placement === "grid" && banner.anchorSlug === category.id)
        }))
        .filter(group => group.items.length > 0),
    [categories, products, banners]
  );

  const topBanners = useMemo(() => banners.filter(banner => banner.placement === "top"), [banners]);

  const scrollTo = useCallback((categoryId: string) => {
    const node = sections.current.get(categoryId);
    if (!node) return;

    /*
     * Отступ под липкими шапкой и полосой категорий задан в стилях, через
     * `scroll-margin-top`. Считать его здесь значило бы держать одно и то же
     * число в двух местах и однажды поправить только одно из них.
     */
    const before = window.scrollY;
    node.scrollIntoView({ behavior: "smooth", block: "start" });

    /*
     * Плавную прокрутку поддерживает не всякий движок, и отказывает он молча:
     * страница просто остаётся на месте. Для гостя это выглядит так, будто
     * раздел пустой или меню сломано, — поэтому через кадр проверяем, сдвинулась
     * ли страница, и если нет, прыгаем сами.
     */
    window.setTimeout(() => {
      if (Math.abs(window.scrollY - before) < 2) node.scrollIntoView({ block: "start" });
    }, 150);
  }, []);

  /*
   * Активный раздел определяется прокруткой, а не нажатием.
   *
   * Гость листает меню пальцем чаще, чем целится в полосу категорий, и полоса
   * обязана за ним успевать: иначе она показывает раздел, который уехал с
   * экрана пять секунд назад.
   */
  useEffect(() => {
    const nodes: HTMLElement[] = [];
    for (const group of grouped) {
      const node = sections.current.get(group.category.id);
      if (node) nodes.push(node);
    }

    if (nodes.length === 0) return;

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];

        if (visible) setActiveCategory(visible.target.id.replace("section-", ""));
      },
      /*
       * Верхняя граница опущена под липкую шапку, нижняя поднята почти к низу
       * экрана: активным считается тот раздел, который сейчас читают, а не
       * тот, чей край едва показался снизу.
       */
      { rootMargin: "-140px 0px -70% 0px", threshold: 0 }
    );

    for (const node of nodes) observer.observe(node);
    return () => observer.disconnect();
  }, [grouped]);

  /*
   * Полоса категорий едет за прокруткой.
   *
   * Разделов одиннадцать, в полосу помещается три. Без этого гость, долистав
   * до лимонадов, видит в полосе корн-доги и хот-доги — то есть подсказку о
   * том, где он был, а не о том, где он сейчас.
   */
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || !activeCategory) return;

    const index = grouped.findIndex(group => group.category.id === activeCategory);
    const pill = rail.children[index] as HTMLElement | undefined;
    if (!pill) return;

    const gap = 16;
    const left = pill.offsetLeft - gap;
    const right = pill.offsetLeft + pill.offsetWidth + gap;

    // Полоса двигается, только когда пилюля вышла за край: дёргать её на
    // каждом кадре прокрутки — значит мешать тому, кто листает её сам.
    if (left < rail.scrollLeft) rail.scrollLeft = left;
    else if (right > rail.scrollLeft + rail.clientWidth) rail.scrollLeft = right - rail.clientWidth;
  }, [activeCategory, grouped]);

  const openBanner = useCallback(
    (banner: Banner) => {
      if (banner.action === "category" && banner.targetSlug) {
        scrollTo(banner.targetSlug);
        return;
      }

      if (banner.action === "product" && banner.targetSlug) {
        const product = products.find(item => item.id === banner.targetSlug);
        if (product) setChosen(product);
      }
    },
    [products, scrollTo]
  );

  if (!state || !benchChecked) return <Spinner text="Загружаем меню…" />;

  const front = state.storefront;
  const accepting = state.mode.accepting;
  const benchReady = Boolean(bench && bench.status === "active");
  const canOrder = accepting && benchReady;

  return (
    <div className="shell">
      <header className="app-bar">
        <a className="brand" href="/">
          {front.logoUrl ? <img className="brand-logo" src={front.logoUrl} alt={front.title} /> : null}
          <span>
            <b>{front.title}</b>
            {front.tagline ? <small>{front.tagline}</small> : null}
          </span>
        </a>

        {bench && bench.status === "active" ? (
          <span className="bar-bench" title={`Лавочка №${bench.label}`}>
            <small>Лавочка</small>
            <b className="num">{bench.label}</b>
          </span>
        ) : null}
      </header>

      <main className="page">
        <BenchBanner bench={bench} benchRef={benchRef} accepting={accepting} message={state.mode.message} />

        {topBanners.length > 0 ? (
          <section className="banner-rail" aria-label="Объявления">
            {topBanners.map(banner => (
              <BannerWide banner={banner} key={banner.id} onOpen={openBanner} />
            ))}
          </section>
        ) : null}

        {state.promotions.length > 0 && canOrder ? (
          <section className="promo-strip" aria-label="Действующие акции">
            {state.promotions.map(promotion => (
              <article className="promo-chip" key={promotion.id}>
                <b>{promotion.title}</b>
                {promotion.description ? <small>{promotion.description}</small> : null}
              </article>
            ))}
          </section>
        ) : null}

        <nav className="cat-nav" aria-label="Разделы меню" ref={railRef}>
          {grouped.map(group => (
            <button
              className={`cat ${activeCategory === group.category.id ? "active" : ""}`}
              type="button"
              key={group.category.id}
              onClick={() => scrollTo(group.category.id)}
            >
              {group.category.title}
            </button>
          ))}
        </nav>

        {grouped.length === 0 ? <p className="muted">Меню пока пустое.</p> : null}

        {grouped.map(group => (
          <section
            className="menu-section"
            id={`section-${group.category.id}`}
            key={group.category.id}
            ref={node => {
              if (node) sections.current.set(group.category.id, node);
              else sections.current.delete(group.category.id);
            }}
          >
            {group.before.map(banner => (
              <BannerWide banner={banner} key={banner.id} onOpen={openBanner} />
            ))}

            <h2>{group.category.title}</h2>

            <div className="grid">
              {withBanners(group.items, group.inside).map(cell =>
                "banner" in cell ? (
                  <BannerTile banner={cell.banner} key={`banner-${cell.banner.id}`} onOpen={openBanner} />
                ) : (
                  <DishCard
                    key={cell.product.id}
                    product={cell.product}
                    canOrder={canOrder}
                    onPick={() => setChosen(cell.product)}
                    onQuickAdd={() => cart.add(cell.product, [])}
                  />
                )
              )}
            </div>
          </section>
        ))}

        {front.footerText ? <p className="footer-note">{front.footerText}</p> : null}
      </main>

      {cart.count > 0 ? (
        <div className="dock">
          <button className="wide-btn" type="button" onClick={() => setCartOpen(true)}>
            Корзина · {cart.count} поз. · {money(cart.totals.total)}
          </button>
        </div>
      ) : null}

      {chosen ? (
        <ProductSheet
          product={chosen}
          onClose={() => setChosen(null)}
          onAdd={(modifiers, qty) => {
            cart.add(chosen, modifiers, qty);
            setChosen(null);
          }}
        />
      ) : null}

      {cartOpen ? (
        <CartSheet
          cart={cart}
          bench={bench}
          benchRef={benchRef}
          minOrderAmount={state.settings.minOrderAmount}
          canOrder={canOrder}
          promoCode={promoCode}
          onPromoCode={setPromoCode}
          onClose={() => setCartOpen(false)}
        />
      ) : null}
    </div>
  );
}

type Cell = { product: ProductDto } | { banner: Banner };

/**
 * Баннеры встают в сетку на то место, которое назначил им владелец.
 *
 * Номер порядка здесь — это номер ячейки, а не «чем меньше, тем выше»: место
 * баннера среди блюд видно глазом, и управлять владелец должен именно им.
 * Номер больше числа блюд означает конец раздела, а не ошибку: меню меняется
 * чаще баннеров, и вчерашней третьей ячейки сегодня может не быть.
 */
function withBanners(products: ProductDto[], banners: Banner[]): Cell[] {
  const cells: Cell[] = products.map(product => ({ product }));

  for (const banner of banners) {
    const at = Math.min(banner.sort, cells.length);
    cells.splice(at, 0, { banner });
  }

  return cells;
}

/** Свои цвета баннера. Пусто — берётся акцент витрины. */
function bannerStyle(banner: Banner): CSSProperties {
  const style: Record<string, string> = {};
  if (banner.colors.bg) style["--banner-bg"] = banner.colors.bg;
  if (banner.colors.ink) style["--banner-ink"] = banner.colors.ink;
  return style as CSSProperties;
}

/**
 * Баннер без перехода — не кнопка.
 *
 * Кнопка, которая ничего не открывает, читается как поломка: гость жмёт,
 * ничего не происходит, и он решает, что сломана вся страница. Поэтому
 * объявление остаётся текстом, а нажимается только то, что куда-то ведёт.
 */
function BannerWide({ banner, onOpen }: { banner: Banner; onOpen: (banner: Banner) => void }) {
  const inside = (
    <>
      {banner.imageUrl ? <img className="banner-photo" src={banner.imageUrl} alt="" loading="lazy" /> : null}
      <span className="banner-copy">
        <b>{banner.title}</b>
        {banner.text ? <small>{banner.text}</small> : null}
      </span>
    </>
  );

  if (banner.action === "none") {
    return (
      <div className="banner wide" style={bannerStyle(banner)}>
        {inside}
      </div>
    );
  }

  return (
    <button className="banner wide" type="button" style={bannerStyle(banner)} onClick={() => onOpen(banner)}>
      {inside}
    </button>
  );
}

/** Баннер на месте блюда — той же величины, что карточка рядом. */
function BannerTile({ banner, onOpen }: { banner: Banner; onOpen: (banner: Banner) => void }) {
  const inside = (
    <>
      {banner.imageUrl ? <img className="banner-photo" src={banner.imageUrl} alt="" loading="lazy" /> : null}
      <span className="banner-copy">
        <b>{banner.title}</b>
        {banner.text ? <small>{banner.text}</small> : null}
      </span>
    </>
  );

  if (banner.action === "none") {
    return (
      <div className="banner tile" style={bannerStyle(banner)}>
        {inside}
      </div>
    );
  }

  return (
    <button className="banner tile" type="button" style={bannerStyle(banner)} onClick={() => onOpen(banner)}>
      {inside}
    </button>
  );
}

/** Плашка лавочки — главное обещание сервиса и первое, что видит гость. */
function BenchBanner({
  bench,
  benchRef,
  accepting,
  message
}: {
  bench: Bench | null;
  benchRef: string;
  accepting: boolean;
  message: string;
}) {
  if (!bench) {
    return (
      <div className="bench-bar warn">
        <span className="bench-num">?</span>
        <span>
          <b>{benchRef ? "Лавочка не найдена" : "Лавочка не определена"}</b>
          <small>
            {benchRef
              ? "Проверьте код на наклейке или отсканируйте её ещё раз."
              : "Отсканируйте код на лавочке — тогда мы будем знать, куда нести заказ."}
          </small>
        </span>
      </div>
    );
  }

  if (bench.status !== "active") {
    return (
      <div className="bench-bar warn">
        <span className="bench-num">{bench.label}</span>
        <span>
          <b>Эта лавочка временно отключена</b>
          <small>Пересядьте на соседнюю и отсканируйте её код.</small>
        </span>
      </div>
    );
  }

  if (!accepting) {
    return (
      <div className="bench-bar warn">
        <span className="bench-num">{bench.label}</span>
        <span>
          <b>Сейчас мы не принимаем заказы</b>
          <small>{message || "Загляните к нам позже."}</small>
        </span>
      </div>
    );
  }

  return (
    <div className="bench-bar">
      <span className="bench-num">{bench.label}</span>
      <span>
        <b>Принесём к лавочке №{bench.label}</b>
        <small>Доставка бесплатно · заказ соберём и принесём сюда</small>
      </span>
    </div>
  );
}

function DishCard({
  product,
  canOrder,
  onPick,
  onQuickAdd
}: {
  product: ProductDto;
  canOrder: boolean;
  onPick: () => void;
  onQuickAdd: () => void;
}) {
  const needsChoice = product.modifiers.length > 0;
  const available = product.available && canOrder;

  return (
    <article className={`dish ${product.available ? "" : "off"}`}>
      <button
        className="dish-photo"
        type="button"
        onClick={onPick}
        aria-label={`Открыть «${product.title}»`}
        disabled={!available}
      >
        {product.badges.length > 0 ? (
          <div className="badges">
            {product.badges.map(badge => (
              <span className={`badge ${badge.toLowerCase() === "хит" ? "hit" : ""}`} key={badge}>
                {badge}
              </span>
            ))}
          </div>
        ) : null}
        <Photo src={product.imageUrl} title={product.title} />
      </button>

      <h3>{product.title}</h3>
      {product.description ? <p className="desc">{product.description}</p> : null}

      <button
        className="price-btn"
        type="button"
        disabled={!available}
        onClick={needsChoice ? onPick : onQuickAdd}
      >
        {product.available ? (
          <>
            <span className="num">{money(product.price)}</span>
            {product.oldPrice ? <span className="old-price num">{money(product.oldPrice)}</span> : null}
          </>
        ) : (
          "Закончился"
        )}
      </button>
    </article>
  );
}
